import type * as Geo from "@/base/geo"
import type {
  ConfigSensorName,
  HardwareGeneration,
  InstallationType,
  ParticlePlatform,
  ParticleProductId,
  SensorConfig,
  SensorName,
} from "@/base/sensor-configurations"
import type { DeviceSortKey, SortDirectionKey } from "@/base/Sorting"
import type { Reducer } from "@reduxjs/toolkit"
import type { SetOptional } from "type-fest"

import * as AppStorage from "@/base/async-storage"
import {
  addDeviceAsync,
  createConfigurationAsync,
  removeDeviceFromFarmAsync,
  renameDeviceAsync,
  replaceDeviceAsync,
  restoreConfigurationDefaultsAsync,
  setDeviceLocationPermanentAsync,
  clearDeviceUnreachableAsync,
  setDeviceMuteStatusAsync,
} from "@/base/farmhq-api"
import { loadActiveFarmAsync } from "@/base/load-app"
import { formatInstallationType } from "@/base/sensor-configurations"
import { isMutableSensorName } from "@/base/sensors"
import * as Models from "@/redux/models"
import { createSlice } from "@reduxjs/toolkit"

import { signOutAsync } from "./auth.reducer"
import type { RootState } from "@/redux/root.reducer"

export interface DeviceSummary {
  codaDeviceAlias: string
  configurationId?: number | null
  deviceId: string
  deviceInstallationType: InstallationType
  deviceName: string
  firmwareVersion: number | null
  gpsLocation: Geo.MultiPoint | null | undefined
  hardwareGeneration: HardwareGeneration
}

interface SensorConfigurations {
  battery?: SensorConfig<"battery"> | null
  depth?: SensorConfig<"depth"> | null
  device?: undefined
  flow?: SensorConfig<"flow"> | null
  gps?: SensorConfig<"gps"> | null
  hallSwitch?: SensorConfig<"hallSwitch"> | null
  humidity?: SensorConfig<"humidity"> | null
  icm?: SensorConfig<"icm"> | null
  pressure?: SensorConfig<"pressure"> | null
  pressureSwitch?: SensorConfig<"pressureSwitch"> | null
  reel?: SensorConfig<"reel"> | null
  relay?: SensorConfig<"relay"> | null
  temperature?: SensorConfig<"temperature"> | null
  vfd?: SensorConfig<"vfd"> | null
  wheel?: SensorConfig<"wheel"> | null
}

export interface DeviceConfiguration
  extends SensorConfigurations,
    SetOptional<Omit<DeviceSummary, "gpsLocation">, "deviceName"> {
  centerPivotId?: number | null
  createDate?: string
  createdByUserId?: string | null
  deactivateDate?: string | null
  deactivatedByUserId?: string | null
  devicePlatform?: number | string
  deviceProductId?: ParticleProductId
  id: number
  isActive?: boolean
  linearPath?: Array<{
    coordinates: Geo.Coordinates
    label?: string | null
  }> | null
  linearPathStopsLabels?: string[] | null
  linearSpanHeadingDegrees?: number | null
  linearSpanWidthMm?: number | null
  locationPermanent?: Geo.PointGeoJson | null
  notificationsMuted?: boolean
  notificationsMutedAt?: string | null
  notificationsMutedByUserEmail?: string | null
  unreachableUntil?: string | null
  consecutiveTimeoutCount?: number
  particlePlatform?: ParticlePlatform
  /**
   * @deprecated Pivot geometry (center, radius, path stops) moved to the
   * CenterPivot model in backend migration 0266. These fields are no longer
   * returned by the API — they'll always be undefined at runtime. Read pivot
   * geometry from the linked CenterPivot via `centerPivotId` / the center-
   * pivot-selectors instead. Kept in the interface to avoid a large-scope
   * refactor of every call site in this PR; to be removed in a follow-up.
   */
  pivotCenterGpsLocation?: Geo.PointGeoJson | null
  /** @deprecated See pivotCenterGpsLocation. */
  pivotRadiusMeters?: number | null
}

const adapter = Models.deviceConfiguration.adapter

export interface DeviceConfigurationState
  extends Models.ModelState<DeviceConfiguration, string> {
  deviceRoster: { sortDirection: SortDirectionKey; sortKey: DeviceSortKey }
  // selectedConfigFieldName?: ConfigurableSensorKey &
  //   SensorConfigKey<MutableSensorName>

  // showNoConfiguredSensors?: boolean
}

const initialState: DeviceConfigurationState = {
  ...Models.getInitialEntityState(),
  deviceRoster: {
    sortDirection: AppStorage.STORAGE_DEFAULTS.deviceSortDirection,
    sortKey: AppStorage.STORAGE_DEFAULTS.deviceSortKey,
  },
}
const devicesSlice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(AppStorage.setDeviceSortDirection.pending, (state, action) => {
        state.deviceRoster.sortDirection = action.meta.arg
      })
      .addCase(AppStorage.setDeviceSortKey.pending, (state, action) => {
        state.deviceRoster.sortKey = action.meta.arg
      })
      .addCase(signOutAsync.fulfilled, () => {
        return { ...initialState }
      })
      .addCase(setDeviceLocationPermanentAsync.fulfilled, (state, action) => {
        const { deviceId, locationPermanent } = action.meta.arg
        adapter.updateOne(state, {
          changes: { locationPermanent },
          id: deviceId,
        })
      })
      .addCase(addDeviceAsync.fulfilled, (state, { payload }) => {
        if (payload.status === "VALID" && payload.deviceConfiguration) {
          adapter.upsertOne(state, payload.deviceConfiguration)
        }
      })
      .addCase(replaceDeviceAsync.fulfilled, (state, action) => {
        if (action.payload.status === "VALID") {
          if (action.payload.deviceConfiguration) {
            adapter.upsertOne(state, action.payload.deviceConfiguration)
          }
          adapter.removeOne(state, action.meta.arg.deviceIdOld)
        }
      })
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          state.deviceRoster = {
            sortDirection: payload.deviceSortDirection,
            sortKey: payload.deviceSortKey,
          }
        },
      )
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        adapter.setAll(state, payload.deviceConfigurations ?? [])
      })
      .addCase(
        restoreConfigurationDefaultsAsync.fulfilled,
        /**
         * On success, given device config is replaced with a 'blank'
         * configuration containing only minimum sensor configuration based
         * on device generation. Installation type is 'unconfigured'
         */
        (state, { payload }) => {
          if (payload && payload.result) {
            adapter.setOne(state, payload.result)
          }
        },
      )

      .addCase(
        renameDeviceAsync.fulfilled,
        /**
         * Device name is changed in place; configuration is kept
         *
         * @param state
         * @param arg
         * @param args.meta
         * @param args.payload
         */
        (state, action) => {
          adapter.updateOne(state, {
            changes: { deviceName: action.meta.arg.deviceName },
            id: action.meta.arg.deviceId,
          })
        },
      )
      .addCase(removeDeviceFromFarmAsync.fulfilled, (state, { meta }) => {
        adapter.removeOne(state, meta.arg.deviceId)
      })
      .addCase(createConfigurationAsync.fulfilled, (state, { payload }) => {
        adapter.setOne(state, payload)
      })
      .addCase(setDeviceMuteStatusAsync.fulfilled, (state, { payload }) => {
        adapter.updateOne(state, {
          changes: {
            notificationsMuted: payload.notificationsMuted,
            notificationsMutedAt: payload.notificationsMutedAt ?? null,
            notificationsMutedByUserEmail:
              payload.notificationsMutedByUserEmail ?? null,
          },
          id: payload.deviceId,
        })
      })
      .addCase(
        clearDeviceUnreachableAsync.fulfilled,
        (state, { payload }) => {
          adapter.updateOne(state, {
            changes: {
              unreachableUntil: payload.unreachableUntil ?? null,
              consecutiveTimeoutCount: payload.consecutiveTimeoutCount ?? 0,
            },
            id: payload.deviceId,
          })
        },
      ),

  initialState,
  name: `deviceConfigurations`,
  reducers: {},
})

const deviceConfigurations: Reducer<DeviceConfigurationState> =
  devicesSlice.reducer

export default deviceConfigurations

/**
 *
 */
export function isSensorCompatibleWithConfiguration({
  configuration,
  sensorName,
}: {
  configuration:
    | Pick<
        DeviceConfiguration,
        "deviceInstallationType" | "hardwareGeneration"
      >
    | null
    | undefined
  sensorName: SensorName
}): boolean {
  let isCompatible = true
  if (!configuration) {
    return false
  }

  // A wheel is never listed as incompatible below. It is required telemetry
  // on travellers/pivots/linears, but any device may carry one as an optional
  // extra sensor (e.g. a paddle wheel on a pump), so its presence is the
  // operator's call rather than something the installation type dictates.
  switch (configuration.deviceInstallationType) {
    case "reel_with_booster_off_only":
    case "reel": {
      break
    }
    case "center_pivot":
    case "linear_move":
    case "traveller_soft": {
      if (sensorName === "reel" || sensorName === "hallSwitch") {
        isCompatible = false
      }
      break
    }
    case "valve": {
      if (
        sensorName === "flow" ||
        sensorName === "pressure" ||
        sensorName === "pressureSwitch" ||
        sensorName === "depth"
      ) {
        isCompatible = true
      }
      break
    }
    case "pump_vfd":
    case "pump_on_off":
    case "pump_off_only":
    case "pump": {
      if (sensorName === "reel" || sensorName === "hallSwitch") {
        isCompatible = false
      }
      break
    }
    case "prototype": {
      break
    }
    case "unconfigured": {
      isCompatible = false
      break
    }
  }
  if (
    configuration.hardwareGeneration !== "PC1" &&
    sensorName === "pressureSwitch"
  ) {
    isCompatible = false
  }
  return isCompatible
}

/**
 * For a given installation type, determine whether a user should be to
 * delete the given sensor
 */
export function canDeleteSensor({
  deviceInstallationType,
  sensorName,
}: {
  deviceInstallationType: InstallationType | null | undefined
  sensorName: SensorName
}) {
  let canDelete = isMutableSensorName(sensorName)
  switch (deviceInstallationType) {
    case null:
    case undefined: {
      break
    }
    case "traveller_soft":
    case "center_pivot":
    case "linear_move": {
      if (sensorName === "wheel") {
        canDelete = false
      }
      break
    }
    case "prototype": {
      break
    }
    case "valve":
    case "pump_off_only":
    case "pump_on_off":
    case "pump_vfd":
    case "pump": {
      break
    }
    case "reel_with_booster_off_only":
    case "reel": {
      if (sensorName === "reel") {
        canDelete = false
      }
      break
    }
    case "unconfigured": {
      break
    }
  }
  return canDelete
}
export function formatDeviceSelectOption(device: DeviceConfiguration) {
  const name = device.deviceName ?? device.codaDeviceAlias
  let text = name
  if (device.deviceName !== device.codaDeviceAlias) {
    text += ` (${device.codaDeviceAlias}) `
  }
  text += ` - ${formatInstallationType(device.deviceInstallationType)}`
  return text
}

export function makeDeviceConfigurationCacheKey(
  state: RootState,
  deviceId: string,
) {
  const configuration = Models.deviceConfiguration.selectById(state, deviceId)
  if (configuration) {
    return configuration.id
  }
  return deviceId
}

export function getConfiguredSensorsForDevice(
  state: RootState,
  deviceId: string
): Array<SensorConfig<ConfigSensorName>> {
  const config = Models.deviceConfiguration.selectById(state, deviceId)
  if (!config) return []

  return Object.entries(config)
    .filter(([_, value]) => value && typeof value === "object" && "sensorName" in value)
    .map(([_, value]) => value as SensorConfig<ConfigSensorName>)
}
